
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { KIOSK_FIELD_ROLES, MAX_INDIVIDUAL_LOCATIONS } from '@/utils/constants';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { toErrorMessage } from '@/utils/errorHandling';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import Autocomplete from '@/components/shared/Autocomplete.vue';
import UserForm from '@/components/user-management/UserForm.vue';
import LocationAssignments from '@/components/user-management/LocationAssignments.vue';

@Component<CreateUserModal>({
	components: {
		Autocomplete,
		LocationAssignments,
		StandardModal,
		UserForm
	}
})
export default class CreateUserModal extends Vue {
	@Action('createUser', { namespace: 'users' }) private createUser!: (payload: CreateUserPayload) => Promise<void>;
	@Action('fetchLocationAssignments', { namespace: 'users' }) private fetchLocationAssignments!: () => Promise<LocationAssignment[] | undefined>;
	@Action('fetchUsers', { namespace: 'users' }) private fetchUsers!: (params?: FetchUsersFilters & { commitToStore?: boolean }) => Promise<Auth0User[] | undefined>;
	@Action('fetchUserById', { namespace: 'users' }) private fetchUserById!: (userId: string) => Promise<Auth0User | undefined>;
	@Prop({ type: Boolean, default: false }) private showModal!: boolean;
	@Watch('locationAssignmentMode')
	private onAssignmentModeChange(): void {
		this.locationsPayload = { groups: [], restaurants: [] };
		// Preload the full user list when selecting "copy mode" so the dropdown isn't empty on first open.
		if (this.locationAssignmentMode === 'copy') {
			this.userSearchQuery = '';
			this.searchUsers();
		}
	}

	$refs!: { form: UserForm; };

	private readonly maxIndividualLocations: number = MAX_INDIVIDUAL_LOCATIONS;
	private formValue: UserFormValue = {
		email: '',
		password: '',
		role: '',
		kioskNumber: '',
		pins: ''
	};
	private isSubmitting: boolean = false;
	private isLoadingAssignments: boolean = true;
	private isSearchingUsers: boolean = false;
	private isLoadingCopyAssignments: boolean = false;
	private locationAssignmentMode: 'manual' | 'copy' = 'manual';
	private userSearchQuery: string = '';
	private selectedUserId: string | null = null;
	private operatorLocationAssignments: LocationAssignment[] = [];
	private copyUserLocationAssignments: LocationAssignment[] = [];
	private locationsPayload: LocationAssignmentsPayload = { groups: [], restaurants: [] };
	private userResults: Auth0User[] = [];

	// Fetch the locations of the operator so we can assign them to the new user
	private async created(): Promise<void> {
		try {
			this.operatorLocationAssignments = (await this.fetchLocationAssignments()) ?? [];
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.user_management.create.error_loading_locations', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isLoadingAssignments = false;
		}
	}

	private beforeDestroy(): void {
		cancelDebounce(this.runUserSearch);
	}

	/**
	 * Handle typing in the "Search user" field: store the query and trigger a debounced search.
	 *
	 * @param {string | null} query
	 * @return {void}
	 */
	private onUserSearchInput(query: string | null): void {
		this.userSearchQuery = (query ?? '').trim();
		this.runUserSearch();
	}

	/**
	 * Debounced wrapper around searchUsers, for the search input's keystrokes.
	 *
	 * @return {void}
	 */
	@Debounce(500)
	private runUserSearch(): void {
		this.searchUsers();
	}

	/**
	 * Search users by the current query and use the response to populate the
	 * dropdown options, without committing to the store.
	 *
	 * @return {Promise<void>}
	 */
	private async searchUsers(): Promise<void> {
		this.isSearchingUsers = true;
		try {
			const users = await this.fetchUsers({
				search: this.userSearchQuery,
				limit: 50,
				sortDirection: 'asc',
				commitToStore: false
			});
			this.userResults = users ?? [];
		}
		catch (error) {
			this.userResults = [];
			Toast.error(this.$t('multi_location_settings.user_management.error_fetching', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isSearchingUsers = false;
		}
	}

	/**
	 * When a user is picked in the search dropdown, fetch their assignments and populate the locations assignment data.
	 *
	 * @param {string | null} userId
	 * @return {Promise<void>}
	 */
	private async onUserSelected(userId: string | null): Promise<void> {
		if (!userId) {
			this.copyUserLocationAssignments = [];
			this.locationsPayload = { groups: [], restaurants: [] };
			return;
		}

		this.isLoadingCopyAssignments = true;
		try {
			const user = await this.fetchUserById(userId);
			// Remove non-editable groups/locations, an operator cannot assign what they don't have access to
			this.copyUserLocationAssignments = (user?.location_assignments ?? [])
				.filter(entry => entry.editable)
				.map(entry => entry.type === 'group'
					? { ...entry, locations: entry.locations.filter(location => location.editable) }
					: entry)
				.filter(entry => entry.type !== 'group' || entry.locations.length > 0);
		}
		catch (error) {
			this.copyUserLocationAssignments = [];
			Toast.error(this.$t('multi_location_settings.user_management.create.error_loading_locations', { errorMessage: toErrorMessage(error) }));
		}
		finally {
			this.isLoadingCopyAssignments = false;
		}
	}

	/**
	 * Create a new user.
	 *
	 * @return {Promise<void>}
	 */
	private async saveUser(): Promise<void> {
		const isValid = await this.$refs.form.validate();
		if (!isValid) {
			return;
		}

		// app_metadata.restaurants limit check
		if (this.locationsPayload.restaurants.length > this.maxIndividualLocations) {
			Toast.error(this.$t('multi_location_settings.user_management.location_assignments.limit_error', { limit: this.maxIndividualLocations }));
			return;
		}

		this.isSubmitting = true;
		try {
			const payload: CreateUserPayload = {
				email: this.formValue.email,
				password: this.formValue.password,
				role: this.formValue.role,
				groups: this.locationsPayload.groups,
				restaurants: this.locationsPayload.restaurants
			};
			// Staff-only values
			if (KIOSK_FIELD_ROLES.includes(this.formValue.role)) {
				payload.kiosk_number = this.formValue.kioskNumber;
				payload.pins = this.formValue.pins
					? this.formValue.pins.split(',').map(p => p.trim()).filter(p => p.length)
					: undefined;
			}
			await this.createUser(payload);
			this.$emit('created');
		}
		catch (error) {
			Toast.error(this.$t('multi_location_settings.user_management.create.error_creating', { errorMessage: toErrorMessage(error) }));
			this.isSubmitting = false;
		}
	}
}
